import { useRef } from "react";
import { Camera, ImagePlus, Loader2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, errorText } from "@/lib/partnerApi";
import { resizeImageForEmail } from "@/lib/imageResize";

const OPTIMIZE = { maxWidth: 2560, maxHeight: 2560, quality: 0.85 };

async function uploadOne(leadId, file) {
    const optimized = await resizeImageForEmail(file, OPTIMIZE);
    const fd = new FormData();
    fd.append("file", optimized);
    const { data } = await partnerApi.post(`/obras/${leadId}/photos`, fd);
    if (optimized !== file) {
        const orig = new FormData();
        orig.append("file", file);
        try {
            await partnerApi.post(`/photos/${data.id}/original`, orig);
        } catch {
            toast.warning("A fotografia foi enviada, mas o original não. A versão otimizada fica guardada.");
        }
    }
    return data.id;
}

// Retirar antes de enviar apaga o ficheiro no servidor (que o mantém se já pertencer a algum registo).
const discard = (id) => { partnerApi.delete(`/photos/${id}`).catch(() => {}); };

// Fotografias: câmara ou galeria, pré-visualização, remover antes de enviar. Envia logo ao escolher.
export function PhotoPicker({ leadId, photos, setPhotos, testid = "partner-photos" }) {
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);
    const removedRef = useRef(new Set());

    const patch = (key, fields) => setPhotos((cur) => cur.map((p) => (p.key === key ? { ...p, ...fields } : p)));

    const addFiles = (fileList) => {
        const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
        files.forEach((file) => {
            const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            setPhotos((cur) => [...cur, { key, preview: URL.createObjectURL(file), uploading: true }]);
            uploadOne(leadId, file)
                .then((id) => {
                    if (removedRef.current.has(key)) discard(id);
                    else patch(key, { id, uploading: false });
                })
                .catch((e) => patch(key, { uploading: false, error: errorText(e, "Não foi possível enviar esta fotografia.") }));
        });
    };

    const remove = (key) => {
        removedRef.current.add(key);
        const photo = photos.find((p) => p.key === key);
        if (photo?.id) discard(photo.id);
        setPhotos((cur) => cur.filter((p) => p.key !== key));
    };
    const onPick = (e) => { addFiles(e.target.files); e.target.value = ""; };

    return (
        <div data-testid={testid}>
            <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => cameraRef.current?.click()} className="flex items-center justify-center gap-2 min-h-[56px] rounded-2xl border-2 border-dashed border-stone-300 bg-white text-[hsl(var(--forest))] font-semibold hover:border-[hsl(var(--copper))] transition-colors" data-testid={`${testid}-camera`}>
                    <Camera className="w-5 h-5" /> Tirar fotografia
                </button>
                <button type="button" onClick={() => galleryRef.current?.click()} className="flex items-center justify-center gap-2 min-h-[56px] rounded-2xl border-2 border-dashed border-stone-300 bg-white text-[hsl(var(--forest))] font-semibold hover:border-[hsl(var(--copper))] transition-colors" data-testid={`${testid}-gallery`}>
                    <ImagePlus className="w-5 h-5" /> Escolher
                </button>
            </div>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid={`${testid}-camera-input`} />
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
            {photos.length > 0 && (
                <ul className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`${testid}-list`}>
                    {photos.map((p) => (
                        <li key={p.key} className="relative aspect-square rounded-xl overflow-hidden bg-stone-100" data-testid={`${testid}-item`}>
                            <img src={p.preview} alt="" className="w-full h-full object-cover" />
                            {p.uploading && (
                                <span className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></span>
                            )}
                            {p.error && (
                                <span className="absolute inset-0 bg-red-900/70 text-white text-[11px] p-2 flex flex-col items-center justify-center text-center gap-1">
                                    <AlertCircle className="w-4 h-4" />{p.error}
                                </span>
                            )}
                            <button type="button" onClick={() => remove(p.key)} className="absolute top-1 right-1 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" aria-label="Remover fotografia" data-testid={`${testid}-remove`}>
                                <X className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export const photoIds = (photos) => photos.filter((p) => p.id && !p.error).map((p) => p.id);
export const photosBusy = (photos) => photos.some((p) => p.uploading);
